import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usePathname, useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import { CardType, IRevaultInformation } from 'features/payment/types';
import {
  isCashApp,
  isPayPal,
  isVenmo,
  mapCardTypeToLoyalty,
  mapPaymentMethodCardTypes,
  shouldReVault,
} from 'features/payment/utils';
import { useStoreContext } from 'features/store';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { getStoreLocalTime } from 'features/store/utils';
import { GestureResponderEvent, View } from 'react-native';

import { CardModal } from 'components/card-modal';
import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import PaymentMethod from 'components/payment-method';
import { isCreditCardPaymentMethod } from 'components/payment-method-option/utils';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { getCurrentSelectedMethodType } from 'components/payment-method/utils';
import { CashAppButton } from 'components/payments/integrations/firstdata/cashapp/cashapp-button';
import PayPalButton from 'components/payments/integrations/firstdata/paypal-button';
import { PayPalVenmoExtraDisplay } from 'components/payments/integrations/firstdata/paypal-venmo-extra-display';
import { IBraintreeResult, ICashAppResult } from 'components/payments/integrations/firstdata/types';
import { getQueryParams } from 'components/payments/integrations/firstdata/utils';
import VenmoButton from 'components/payments/integrations/firstdata/venmo-button';
import { SafetechEncryption } from 'components/payments/integrations/orbital/components/encryption';
import {
  IEncryptionError,
  IEncryptionResult,
} from 'components/payments/integrations/orbital/components/encryption/types';
import WarningMessage from 'components/warning-message';
import CreditCardFormInputs from 'experiments/checkout-v2/control/components/credit-card-form-inputs';
import { useTotalTipCalculations } from 'experiments/checkout-v2/control/pages/cart/your-cart/totals/use-total-tip-calculations';
import { CartPaymentCardType, CartPaymentType } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import { useLoyaltyCartEntriesEvaluation } from 'pages/cart/hooks/use-loyalty-cart-entries-evaluation';
import { PlaceOrderButton } from 'pages/cart/place-order-button';
import SectionHeading from 'pages/cart/section-heading';
import { DeliveryCancellationPolicy } from 'pages/cart/your-cart/delivery-cancellation-policy';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { useCartContext } from 'state/cart';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  SupportedCardType,
  defaultPaymentFieldVariation,
  defaultSupportedCardTypes,
} from 'state/launchdarkly/variations';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { getServerOrderSubtotalBeforeOfferDiscounts } from 'state/loyalty/hooks/utils/helpers';
import { useServiceModeContext } from 'state/service-mode';
import logger from 'utils/logger';
import { routes } from 'utils/routing';
import { getLocalizedServiceMode } from 'utils/service-mode';

import PrepaidMethodReload from '../prepaid-method-reload';
import PrepaidTransferBalance from '../prepaid-transfer-balance';
import { ErrorContainer, ErrorMessage, PaymentSelection, WarningContainer } from '../styled';
import { logCartContinueOrder } from '../utils';

import { Totals as OrderPaymentTotals } from './Totals';
import { useOrderPayment } from './use-order-payment';
import { getNativePaymentDisclaimer } from './util';

export const Payment: React.FC = () => {
  const { cartEntries, calculateCartTotal, calculateCartTotalWithDiscount } = useCart();

  // const enableCashApp = useFlag(LaunchDarklyFlag.ENABLE_CASH_APP_PAYMENTS);
  const {
    serverOrder,
    shouldSkipPriceOrder,
    priceOrderSuccess,
    // TODO: BKPE-1956 - unavailableCartEntries
    unavailableCartEntries,
    priceMutationError,
    tipAmount,
    setIsTipLocked,
  } = useCartContext();

  const newPaymentValueIdRef = useRef('');
  const [isDefaultPaymentMethodSelected, setIsDefaultPaymentMethodSelected] = useState(true);

  const isDesktopWeb = useIsDesktopWebBreakpoint();
  const router = useRouter();
  const pathname = usePathname();

  const { store } = useStoreContext();
  const { isServiceModeAvailable } = useStoreAvailabilityFns();

  const {
    addPaymentMethod,
    canUseApplePay,
    canUseGooglePay,
    checkoutPaymentMethodId,
    isFirstData,
    isOrbital,
    setCheckoutPaymentMethodId,
    setEncryptionResult,
    storePaymentDetails,
    clearPaymentDetails,
    transformPaymentValues,
    isCheckoutErrorsModalOpen,
    setIsCheckoutErrorsModalOpen,
    setProcessingPayment,
    setOrderGrandTotal,
  } = usePaymentContext();
  const { serviceMode, isDelivery } = useServiceModeContext();

  const [hasAddPaymentMethodError, setAddPaymentMethodError] = useState(false);
  const { loyaltyUser } = useLoyaltyUser();
  const isCartPayment = pathname === routes.cartPayment;
  const evaluateLoyaltyCartEntries = useLoyaltyCartEntriesEvaluation();
  const evaluatingIncentives = useAppSelector(selectors.loyalty.selectOffersLoading);
  const {
    cardIsValid,
    commitOrder,
    confirmStoreOnCheckout,
    ErrorDialog,
    handlePaymentMethodSelected,
    enableDelivery,
    errors,
    isProcessingPayment,
    onChange,
    isValidReloadPaymentMethodWithCard,
    isValidTip,
    needReloadAgain,
    openServiceModeUnavailableErrorDialog,
    paymentMethods,
    paymentValues,
    placeOrderAriaLabel,
    reloadAmount,
    reloadPaymentValues,
    ServiceModeUnavailableErrorDialog,
    setReloadPaymentValues,
    showAddPaymentMethod: isAddNewCardMethodSelected,
    showReloadPaymentMethods,
    validateCreditCardForm,
    isAddGiftCardMethodSelected,
    setShowAddPaymentMethod,
    showPaymentMethodSelection,
    showPrepaidTransferBalance,
    setIsAddGiftCardMethodSelected,
  } = useOrderPayment();

  const [isEncryptionActive, setIsEncryptionActive] = useState(false);
  const [isOpenPaymentDrawer, setIsOpenPaymentDrawer] = useState(false);
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;
  const enableGiftCard = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD);
  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);
  const rawSupportedCardTypes: SupportedCardType[] =
    useFlag(LaunchDarklyFlag.SUPPORTED_CARD_BRANDS_VARIATIONS) || defaultSupportedCardTypes;
  const enableOnetimePaymentAfterVault = useFlag(
    LaunchDarklyFlag.ENABLE_VAULT_AFTER_MAKING_ONE_TIME_PAYMENT
  );
  const supportedCardTypes = rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];

  const nativePaymentDisclaimer = getNativePaymentDisclaimer(canUseApplePay, canUseGooglePay);
  const isSignUpAfterCart = useIsSignUpAfterCartEnabled();
  const checkoutPriceMinimum = isDelivery ? checkoutDeliveryPriceMinimum : 0;

  const cartSubTotal =
    isSignUpAfterCart || shouldSkipPriceOrder
      ? calculateCartTotal()
      : Number(serverOrder?.cart?.subTotalCents);
  const cartPriceTooLow = cartSubTotal < checkoutPriceMinimum;

  const { getErrorMessages, getLoyaltyPaymentErrorMessages } = useGetPaymentErrors({
    cartPriceTooLow,
  });

  const pricedAndAvailable =
    priceOrderSuccess && !unavailableCartEntries.length && !priceMutationError;
  const paymentErrorMessages = getErrorMessages({
    invalidOfferError: false,
    invalidPaymentMethodError:
      !cardIsValid || !isValidReloadPaymentMethodWithCard || hasAddPaymentMethodError,
    invalidTipAmountError: !isValidTip,
    pricedAndAvailableError: !pricedAndAvailable,
  });

  const { serverOrderTip, cart } = useTotalTipCalculations({ serverOrder, serviceMode });

  const handleAddPaymentMethod = useCallback(
    async (encryptionResult?: IEncryptionResult) => {
      try {
        if (isOrbital && !encryptionResult) {
          throw new Error('Orbital encryption results not available');
        }
        const newPaymentValueId = await addPaymentMethod(
          transformPaymentValues({
            paymentValues,
            paymentFieldVariations,
          }),
          {
            skipErrorDialogOnError: true,
          },
          encryptionResult
        );
        setCheckoutPaymentMethodId(newPaymentValueId);
        handlePaymentMethodSelected(newPaymentValueId);
        return newPaymentValueId;
      } catch (error) {
        setAddPaymentMethodError(true);
        setIsCheckoutErrorsModalOpen(true);
        return null;
      }
    },
    [
      addPaymentMethod,
      isOrbital,
      handlePaymentMethodSelected,
      paymentFieldVariations,
      paymentValues,
      setCheckoutPaymentMethodId,
      setIsCheckoutErrorsModalOpen,
      transformPaymentValues,
    ]
  );

  const loyaltyPaymentMethodErrors = getLoyaltyPaymentErrorMessages();
  const errorMessages = useMemo(() => {
    return [...paymentErrorMessages, ...loyaltyPaymentMethodErrors];
  }, [paymentErrorMessages, loyaltyPaymentMethodErrors]);

  const { isCartTotalNegative = false } = calculateCartTotalWithDiscount?.() || {};
  const positiveCartTotal = isCartTotalNegative
    ? 0
    : getServerOrderSubtotalBeforeOfferDiscounts(serverOrder);

  const handleOnSubmit = useCallback(
    async (event?: GestureResponderEvent, encryptionResult?: IEncryptionResult) => {
      if (confirmStoreOnCheckout && !isDelivery) {
        logCartContinueOrder();
      }
      if (event) {
        event.preventDefault();
      }

      if (isAddNewCardMethodSelected && !validateCreditCardForm(true)) {
        return;
      }

      if (
        isAddNewCardMethodSelected &&
        validateCreditCardForm() &&
        !checkoutPaymentMethodId &&
        paymentValues.saveCard
      ) {
        const skipVaultStep = enableOnetimePaymentAfterVault && isFirstData;
        if (!skipVaultStep) {
          const paymentId = await handleAddPaymentMethod(encryptionResult);

          if (!paymentId) {
            return;
          }

          newPaymentValueIdRef.current = paymentId;
        }
      }
      const loyaltyId = loyaltyUser?.id;
      if (loyaltyId && isAddNewCardMethodSelected) {
        const paymentMethod = mapCardTypeToLoyalty(paymentValues.cardType);

        await evaluateLoyaltyCartEntries(loyaltyId, cartEntries, positiveCartTotal, paymentMethod);
      }

      const serviceModeIsNotAvailableOrIsDisabled = !isServiceModeAvailable(
        store,
        serviceMode,
        getStoreLocalTime()
      );

      if (serviceModeIsNotAvailableOrIsDisabled) {
        openServiceModeUnavailableErrorDialog({
          message: t('currentServiceModeUnavailable', {
            serviceMode: getLocalizedServiceMode(serviceMode),
          }),
        });
        return;
      }

      if (errorMessages.length > 0) {
        setIsCheckoutErrorsModalOpen(true);
      } else if (!isDelivery && confirmStoreOnCheckout) {
        if (!enableOnetimePaymentAfterVault || !isFirstData) {
          // If it is a one time payment with a newly added card, we want to store the info to place the order.
          // Otherwise we clear the localStorage, because `paymentValues.saveCard` can be false due to stale data (see BKPE-256).
          if (!paymentValues.saveCard) {
            if (isAddNewCardMethodSelected) {
              storePaymentDetails({ state: paymentValues });
            } else {
              clearPaymentDetails();
            }
          }
        } else {
          // skipping standalone vault step for first data
          if (isAddNewCardMethodSelected) {
            storePaymentDetails({ state: paymentValues });
          } else {
            clearPaymentDetails();
          }
        }

        // navigation
        router.navigate(routes.cartStoreConfirmation);
      } else {
        commitOrder();
        setProcessingPayment(true);
      }
    },
    [
      confirmStoreOnCheckout,
      isDelivery,
      isAddNewCardMethodSelected,
      validateCreditCardForm,
      checkoutPaymentMethodId,
      paymentValues,
      loyaltyUser?.id,
      isServiceModeAvailable,
      store,
      serviceMode,
      errorMessages.length,
      enableOnetimePaymentAfterVault,
      isFirstData,
      handleAddPaymentMethod,
      evaluateLoyaltyCartEntries,
      cartEntries,
      positiveCartTotal,
      openServiceModeUnavailableErrorDialog,
      setIsCheckoutErrorsModalOpen,
      router,
      storePaymentDetails,
      clearPaymentDetails,
      commitOrder,
      setProcessingPayment,
    ]
  );

  const handlePayPal = useCallback(
    ({ nonce, deviceData, details }: IBraintreeResult) => {
      setCheckoutPaymentMethodId(CartPaymentType.PAYPAL);
      handlePaymentMethodSelected(CartPaymentType.PAYPAL);
      storePaymentDetails({
        accountIdentifier: CartPaymentType.PAYPAL,
        paypalDetails: {
          nonce,
          deviceData,
          details,
        },
      });
      handleOnSubmit();
    },
    [handleOnSubmit, handlePaymentMethodSelected, setCheckoutPaymentMethodId, storePaymentDetails]
  );

  const handlePayPalError = useCallback(
    (error: Error) => {
      logger.error({ error, message: 'PayPal error' });
      if (checkoutPaymentMethodId === CartPaymentType.PAYPAL) {
        setIsCheckoutErrorsModalOpen(true);
      }
    },
    [checkoutPaymentMethodId, setIsCheckoutErrorsModalOpen]
  );

  const handleVenmo = useCallback(
    ({ nonce, deviceData, details }: IBraintreeResult) => {
      setCheckoutPaymentMethodId(CartPaymentType.VENMO);
      handlePaymentMethodSelected(CartPaymentType.VENMO);
      storePaymentDetails({
        accountIdentifier: CartPaymentType.VENMO,
        venmoDetails: {
          details,
          deviceData,
          nonce,
        },
      });
      handleOnSubmit();
    },
    [handleOnSubmit, handlePaymentMethodSelected, setCheckoutPaymentMethodId, storePaymentDetails]
  );

  const handleVenmoError = useCallback(
    (error: Error) => {
      logger.error({ error, message: 'Venmo error' });
      setIsCheckoutErrorsModalOpen(true);
    },
    [setIsCheckoutErrorsModalOpen]
  );

  const handleCashApp = useCallback(
    (result: ICashAppResult) => {
      setCheckoutPaymentMethodId(CartPaymentType.CASHAPP);
      handlePaymentMethodSelected(CartPaymentType.CASHAPP);
      storePaymentDetails({
        accountIdentifier: CartPaymentType.CASHAPP,
        cashappDetails: result,
      });
      handleOnSubmit();
    },
    [handleOnSubmit, handlePaymentMethodSelected, setCheckoutPaymentMethodId, storePaymentDetails]
  );

  const handleCashAppError = useCallback(
    (error: any) => {
      logger.error({ orderId: serverOrder?.rbiOrderId, error, message: 'Cash App error' });
      setIsCheckoutErrorsModalOpen(true);
    },
    [serverOrder?.rbiOrderId, setIsCheckoutErrorsModalOpen]
  );

  const selectedMethod = paymentMethods.find(
    method => method.accountIdentifier === checkoutPaymentMethodId
  );

  const currentSelectedMethodType = getCurrentSelectedMethodType({
    isAddGiftCardMethodSelected,
    isAddNewCardMethodSelected,
    selectedMethodExist: !!selectedMethod,
  });
  const isAddNewPaymentMethodTypeSelected =
    currentSelectedMethodType === CurrentSelectedMethodType.ADD_NEW_PAYMENT_METHOD;

  const isPlaceOrderButtonDisabled =
    isProcessingPayment ||
    (!!selectedMethod?.prepaid && !enableGiftCard) ||
    isAddNewPaymentMethodTypeSelected ||
    (showReloadPaymentMethods && !!paymentMethods.length && !reloadPaymentValues.saveCard);

  // Check if PayPal and Venmo are available
  const isPayPalAvailable = paymentMethods.some(method => isPayPal(method.accountIdentifier));
  const isVenmoAvailable = paymentMethods.some(method => isVenmo(method.accountIdentifier));

  useEffect(() => {
    const { totalCents = 0 } = cart || {};
    const grandTotal = Math.max(totalCents - Number(serverOrderTip) + tipAmount, 0);
    if (!isProcessingPayment) {
      // Setting grand total and checking payments
      setOrderGrandTotal(grandTotal);
    }
  }, [
    cart,
    serverOrderTip,
    tipAmount,
    checkoutPaymentMethodId,
    isProcessingPayment,
    setOrderGrandTotal,
    isCartPayment,
  ]);
  const queryParams = getQueryParams();

  const handleOnPress = useCallback(() => {
    if (isOrbital && !selectedMethod?.accountIdentifier) {
      return setIsEncryptionActive(true);
    }
    const accountIdentifier =
      selectedMethod?.accountIdentifier ?? selectedMethod?.fdAccountId ?? '';
    const revaultInformation: IRevaultInformation = shouldReVault({
      accountIdentifier,
      isOrbitalPaymentEnabled: isOrbital,
      methods: paymentMethods,
    });

    const isRevaultRequired = revaultInformation.isExpired || revaultInformation.isInvalid;

    if (isRevaultRequired) {
      router.navigate(
        `${routes.addCard}?accountToDelete=${selectedMethod?.accountIdentifier}&isInCart=true`
      );
    }
    handleOnSubmit();
  }, [
    handleOnSubmit,
    isOrbital,
    paymentMethods,
    router,
    selectedMethod?.accountIdentifier,
    selectedMethod?.fdAccountId,
  ]);

  const onEncryptionResult = useCallback(
    (data: IEncryptionResult) => {
      setIsEncryptionActive(false);
      setEncryptionResult(data);
      handleOnSubmit(undefined, data);
    },
    [handleOnSubmit, setEncryptionResult]
  );

  // Handle encryption error
  const onEncryptionError = (error: IEncryptionError) => {
    setIsEncryptionActive(false);
    setIsCheckoutErrorsModalOpen(true);
    const errorMessage = error.error;
    logger.error({ error: new Error(errorMessage), message: errorMessage });
  };

  // Logs the Payment Method Selected Event
  useEffect(() => {
    if (selectedMethod) {
      if (isDefaultPaymentMethodSelected) {
        setIsDefaultPaymentMethodSelected(false);
        return;
      }

      let paymentInfo: { paymentMethod: string; cardBrand?: string };
      const isCreditCard = isCreditCardPaymentMethod(selectedMethod);
      if (isCreditCard && selectedMethod.credit) {
        paymentInfo = {
          paymentMethod: CartPaymentCardType.CREDIT,
          cardBrand: selectedMethod.credit.cardType,
        };
      } else if (selectedMethod.prepaid) {
        paymentInfo = {
          paymentMethod: CartPaymentCardType.GIFT_CARD,
        };
      } else {
        paymentInfo = {
          paymentMethod: selectedMethod.accountIdentifier || '',
        };
      }

      logRBIEvent(
        {
          name: CustomEventNames.PAYMENT_METHOD_SELECTED,
          type: EventTypes.Other,
          attributes: paymentInfo,
        },
        { skipLoggingToBraze: true }
      );
    }
  }, [selectedMethod, isDefaultPaymentMethodSelected]);

  useEffect(() => {
    if (queryParams && (queryParams as any)?.isRedirected === 'true') {
      if (checkoutPaymentMethodId !== CartPaymentCardType.CASHAPP) {
        setCheckoutPaymentMethodId(CartPaymentType.CASHAPP);
        handlePaymentMethodSelected(CartPaymentType.CASHAPP);
      }
    }
  }, [
    checkoutPaymentMethodId,
    handlePaymentMethodSelected,
    queryParams,
    setCheckoutPaymentMethodId,
  ]);

  const isLoading = isProcessingPayment || evaluatingIncentives;

  return (
    <View
      testID="cart-payment-layout"
      style={{ flex: 1 }}
      onPointerDown={event => {
        // TODO not sure this does anything wasn't able to get it to work but preserving it here on expo-router refactor
        // Closing payment drawer if opening (only Desktop)
        // @ts-ignore tabIndex prop is not recognized
        if (isDesktopWeb && isOpenPaymentDrawer && event.target.tabIndex === 0) {
          setIsOpenPaymentDrawer(false);
        }
      }}
    >
      <CardModal
        headerText={t('orderPayment')}
        scrollView
        standardPadding
        dismissType="back"
        webAnimate={false}
        invertHeaderColor
        isFullScreen
      >
        {isCheckoutErrorsModalOpen && (
          <CheckoutErrorsModal
            checkoutErrors={errorMessages}
            onClose={() => {
              setIsCheckoutErrorsModalOpen(false);
              setAddPaymentMethodError(false);
            }}
          />
        )}

        <SectionHeading accessible accessibilityRole="header">
          {t('paymentDetails')}
        </SectionHeading>

        {/*TODO replace with auth.user && auth.user.fdCustomerId*/}
        {/* Todo create a separate component to display the selected method */}
        <PaymentSelection>
          <>
            {showPaymentMethodSelection && (
              <PaymentMethod
                aria-expanded="true"
                methods={paymentMethods}
                selectedMethodId={checkoutPaymentMethodId}
                handleSelect={handlePaymentMethodSelected}
                isAddCardOpen={isAddNewCardMethodSelected}
                openAddCardOnPress={setShowAddPaymentMethod}
                isAddGiftCardMethodSelected={isAddGiftCardMethodSelected}
                setIsAddGiftCardMethodSelected={setIsAddGiftCardMethodSelected}
                isOrbitalPaymentEnabled={isOrbital}
              />
            )}

            {isAddNewCardMethodSelected && (
              <CreditCardFormInputs
                ariaLabel="credit-card-form-inputs"
                isDelivery={isDelivery}
                onChange={onChange}
                paymentValues={paymentValues}
                validateCreditCardForm={validateCreditCardForm}
                errors={errors}
                withBorder={false}
                withPadding={false}
                supportedCardTypes={supportedCardTypes}
              />
            )}

            {showPrepaidTransferBalance && <PrepaidTransferBalance />}

            {showReloadPaymentMethods && !!paymentMethods.length && (
              <PrepaidMethodReload
                reloadAmount={reloadAmount}
                reloadPaymentValues={reloadPaymentValues}
                setReloadPaymentValues={setReloadPaymentValues}
                isDelivery={isDelivery}
                orderTotal={serverOrder?.cart?.totalCents ?? 0}
              />
            )}
            {needReloadAgain && (
              <ErrorContainer>
                <ErrorMessage>{t('insufficientBalanceReloadAgain')}</ErrorMessage>
              </ErrorContainer>
            )}

            <OrderPaymentTotals
              serverOrder={serverOrder}
              serviceMode={serviceMode}
              isDisabled={isPlaceOrderButtonDisabled}
              isLoading={isLoading && !isProcessingPayment}
            />

            {!selectedMethod && nativePaymentDisclaimer && (
              <WarningContainer>
                <WarningMessage message={nativePaymentDisclaimer} />
              </WarningContainer>
            )}
            {!(
              selectedMethod &&
              (isPayPal(selectedMethod.accountIdentifier) ||
                isVenmo(selectedMethod.accountIdentifier) ||
                isCashApp(selectedMethod?.accountIdentifier))
            ) && (
              <>
                <PlaceOrderButton
                  confirmation={!confirmStoreOnCheckout || isDelivery}
                  isLoading={isLoading}
                  disabled={isPlaceOrderButtonDisabled || isProcessingPayment}
                  placeOrderAriaLabel={placeOrderAriaLabel}
                  onPress={handleOnPress}
                />
                {!isAddNewCardMethodSelected && !isCashApp(selectedMethod?.accountIdentifier) && (
                  <PayPalVenmoExtraDisplay
                    serverOrder={serverOrder}
                    onPayPalResult={handlePayPal}
                    onPayPalError={handlePayPalError}
                    onVenmoResult={handleVenmo}
                    onVenmoError={handleVenmoError}
                    isPayPalAvailable={isPayPalAvailable}
                    isVenmoAvailable={isVenmoAvailable}
                  />
                )}
              </>
            )}
            {selectedMethod && isPayPal(selectedMethod.accountIdentifier) && (
              <PayPalButton
                serverOrder={serverOrder}
                onResult={handlePayPal}
                onError={handlePayPalError}
                showLoading={isLoading}
              ></PayPalButton>
            )}
            {selectedMethod && isVenmo(selectedMethod.accountIdentifier) && (
              <VenmoButton
                serverOrder={serverOrder}
                onResult={handleVenmo}
                onError={handleVenmoError}
                showLoading={isLoading}
              ></VenmoButton>
            )}
            {selectedMethod && isCashApp(selectedMethod.accountIdentifier) && (
              <CashAppButton
                serverOrder={serverOrder}
                onResult={handleCashApp}
                onError={handleCashAppError}
                showLoading={isLoading}
                tipAmount={tipAmount}
                setIsTipLocked={setIsTipLocked}
              />
            )}
            {isDelivery && enableDelivery && <DeliveryCancellationPolicy />}
          </>
        </PaymentSelection>

        <ErrorDialog />
        <ServiceModeUnavailableErrorDialog testID="servicemode-unavailable-error-modal" />
        {isOrbital && isEncryptionActive ? (
          <SafetechEncryption
            cardNumber={paymentValues.cardNumber}
            cvv={paymentValues.cvv || ''}
            onResult={onEncryptionResult}
            onError={onEncryptionError}
          />
        ) : null}
      </CardModal>
    </View>
  );
};
